import { useCallback, useRef } from 'react';
import { Call } from '@weave/schema-gen-ts/dist/schemas/call-intelligence/v2';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { useGlobalPanel } from '@frontend/panel-engine';
import { useCallTakeawayPanelShallowStore } from './use-call-takeaway-panel-store';

interface CallInsightApi {
  /**
   * Open the panel focused on a single call.
   * Does *not* change the existing list in the store.
   */
  open: (call: Call) => void;

  /**
   * Open the panel with the provided list of calls, and select one.
   */
  openWithList: (call: Call) => void;

  /**
   * Close the Call Takeaway panel.
   */
  close: () => void;

  /**
   * Clear the currently-selected call (and reset any list/navigation state),
   * but does *not* open or close the panel.
   */
  clear: () => void;

  /**
   * Jump to the next call in the currently-loaded list.
   */
  handleNextClick: () => void;

  /**
   * Jump to the previous call in the currently-loaded list.
   */
  handlePreviousClick: () => void;
}
[];
export const useCallInsightController = (): CallInsightApi => {
  const { open: openGlobal, close: closeGlobal } = useGlobalPanel('call-takeaway');
  const {
    setSelectedCall,
    setAllCalls,
    handleNextClick,
    handlePreviousClick,
    reset: resetStore,
  } = useCallTakeawayPanelShallowStore(
    'setSelectedCall',
    'setAllCalls',
    'handleNextClick',
    'handlePreviousClick',
    'reset'
  );

  // Store the list of calls in a ref to avoid unnecessary re-renders
  const callsRef = useRef<Call[]>([]);

  const open = useCallback(
    (call: Call) => {
      setSelectedCall(call as CallIntelTypes.Call);
      openGlobal();
    },
    [openGlobal, setSelectedCall]
  );

  /**
   * @deprecated
   * TODO: This function should not be called unless we fix useCallTakeawayPanelShallowStore to accept context/source
   */
  const openWithList = useCallback(
    (call: Call) => {
      if (!callsRef.current.length) {
        console.warn('No calls are available. Ensure `calls` is provided to the hook.');
        return;
      }

      setSelectedCall(call as CallIntelTypes.Call);
      openGlobal();
    },
    [openGlobal, setSelectedCall]
  );

  const close = useCallback(() => {
    closeGlobal();
  }, [closeGlobal]);

  /**
   * @deprecated
   * TODO: This function should not be called unless we fix useCallTakeawayPanelShallowStore to accept context/source
   */
  const clear = useCallback(() => {
    resetStore();
    callsRef.current = [];
    setAllCalls([]);
  }, [resetStore, setAllCalls]);

  return { open, openWithList, close, clear, handleNextClick, handlePreviousClick };
};
